import { useRef, useState } from "react";
import { FileText, Paperclip, Pin, RotateCcw, ShieldCheck, Upload, X } from "lucide-react";
import { createNotice, updateNotice } from "../../controllers/noticeController";
import { NoticeModal, noticeButton, noticeInput } from "./NoticeParts";
import { NOTICE_CATEGORIES, NOTICE_PRIORITIES, formatFileSize, toLocalInputValue } from "./noticeUtils";

// Limits mirror the server (routes/noticeRoutes.js, services/noticeService.js).
const MAX_TITLE = 200;
const MAX_BODY = 10000;
const MAX_FILES = 5;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const ACCEPT = ".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx";
const ACCEPT_EXT = new Set(ACCEPT.split(",").map((ext) => ext.slice(1)));

const label = "mb-1 block text-sm font-medium text-app-secondary";

function Toggle({ checked, onChange, icon: Icon, title, description }) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${
        checked ? "border-blue-500 bg-blue-50/60 dark:border-blue-500/60 dark:bg-blue-500/10" : "border-app-primary hover:bg-app-hover"
      }`}
    >
      <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${checked ? "text-blue-600 dark:text-sky-400" : "text-app-muted"}`} aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-app-primary">{title}</span>
        <span className="block text-xs text-app-muted">{description}</span>
      </span>
      <span
        aria-hidden="true"
        className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
      </span>
    </label>
  );
}

/** Create a notice (notice = null) or edit one. HR/admins only. */
function NoticeFormModal({ notice, onClose, onSaved }) {
  const isEdit = Boolean(notice);
  const wasLive = isEdit && notice.status !== "scheduled";
  const initialExpiry = toLocalInputValue(notice?.expiresAt);
  const [form, setForm] = useState(() => ({
    title: notice?.title || "",
    body: notice?.body || "",
    category: notice?.category || "general",
    priority: notice?.priority || "normal",
    isPinned: notice?.isPinned || false,
    requiresAck: notice?.requiresAck || false,
    schedule: isEdit && notice.status === "scheduled",
    publishAt: notice?.status === "scheduled" ? toLocalInputValue(notice.publishAt) : "",
    hasExpiry: Boolean(notice?.expiresAt),
    expiresAt: initialExpiry,
  }));
  const [files, setFiles] = useState([]);
  const [removeIds, setRemoveIds] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const fileInput = useRef(null);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const keptAttachments = (notice?.attachments || []).filter((a) => !removeIds.includes(a.id));
  const slotsLeft = MAX_FILES - keptAttachments.length - files.length;

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const problems = [];
    const accepted = [];
    incoming.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!ACCEPT_EXT.has(ext)) problems.push(`${file.name}: unsupported file type`);
      else if (file.size > MAX_FILE_BYTES) problems.push(`${file.name}: larger than 25 MB`);
      else accepted.push(file);
    });
    const room = MAX_FILES - keptAttachments.length - files.length;
    if (accepted.length > room) problems.push(`A notice can have at most ${MAX_FILES} attachments`);
    setFiles((prev) => [...prev, ...accepted.slice(0, Math.max(room, 0))]);
    setError(problems.join(". "));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim() || !form.body.trim()) {
      setError("Add a title and the notice text.");
      return;
    }
    if (form.schedule && !form.publishAt) {
      setError("Pick when the notice should go live.");
      return;
    }
    if (form.hasExpiry && !form.expiresAt) {
      setError("Pick when the notice should be taken down, or turn off the expiry.");
      return;
    }

    const fields = {
      title: form.title.trim(),
      body: form.body,
      category: form.category,
      priority: form.priority,
      isPinned: form.isPinned,
      requiresAck: form.requiresAck,
    };
    if (form.schedule) fields.publishAt = new Date(form.publishAt).toISOString();
    else if (isEdit && !wasLive) fields.publishAt = new Date().toISOString(); // scheduled → publish now
    const expiry = form.hasExpiry ? form.expiresAt : "";
    if (!isEdit || expiry !== initialExpiry) fields.expiresAt = expiry ? new Date(expiry).toISOString() : "";

    setSaving(true);
    setProgress(0);
    try {
      const saved = isEdit
        ? await updateNotice(notice.id, fields, files, removeIds, setProgress)
        : await createNotice(fields, files, setProgress);
      onSaved(saved);
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't save the notice. Please try again.");
      setSaving(false);
    }
  };

  const close = () => !saving && onClose();
  const publishLabel = form.schedule ? "Schedule notice" : isEdit && wasLive ? "Save changes" : "Publish notice";

  return (
    <NoticeModal
      title={isEdit ? "Edit notice" : "New notice"}
      onClose={close}
      width="max-w-3xl"
      footer={
        <>
          {saving && files.length > 0 && (
            <span className="mr-auto text-xs tabular-nums text-app-muted">Uploading… {Math.round(progress * 100)}%</span>
          )}
          <button type="button" className={noticeButton.secondary} onClick={close} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form="notice-form" className={noticeButton.primary} disabled={saving}>
            {saving ? "Saving…" : publishLabel}
          </button>
        </>
      }
    >
      <form id="notice-form" onSubmit={submit} className="space-y-5">
        {error && <p className="rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error">{error}</p>}

        <div>
          <label htmlFor="notice-title" className={label}>
            Title <span className="text-app-error">*</span>
          </label>
          <input
            id="notice-title"
            value={form.title}
            maxLength={MAX_TITLE}
            onChange={(e) => set("title", e.target.value)}
            placeholder="e.g. Office closed on Friday for Independence Day"
            className={noticeInput}
          />
        </div>

        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <label htmlFor="notice-body" className={label}>
              Notice <span className="text-app-error">*</span>
            </label>
            <span className={`text-xs tabular-nums ${form.body.length > MAX_BODY * 0.9 ? "text-app-warning" : "text-app-muted"}`}>
              {form.body.length.toLocaleString()}/{MAX_BODY.toLocaleString()}
            </span>
          </div>
          <textarea
            id="notice-body"
            value={form.body}
            maxLength={MAX_BODY}
            onChange={(e) => set("body", e.target.value)}
            rows={8}
            placeholder="Write the announcement. Line breaks are kept and links become clickable."
            className={`${noticeInput} min-h-[160px] resize-y leading-relaxed`}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="notice-category" className={label}>
              Category
            </label>
            <select
              id="notice-category"
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className={noticeInput}
            >
              {Object.entries(NOTICE_CATEGORIES).map(([key, c]) => (
                <option key={key} value={key}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span className={label} id="notice-priority-label">
              Priority
            </span>
            <div role="radiogroup" aria-labelledby="notice-priority-label" className="grid grid-cols-3 gap-1 rounded-lg bg-app-hover p-1">
              {Object.entries(NOTICE_PRIORITIES).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={form.priority === key}
                  onClick={() => set("priority", key)}
                  className={`rounded-md px-2 py-1.5 text-sm font-medium transition-colors ${
                    form.priority === key
                      ? key === "urgent"
                        ? "bg-red-600 text-white shadow-sm"
                        : key === "important"
                          ? "bg-amber-500 text-white shadow-sm"
                          : "bg-app-card text-app-heading shadow-sm"
                      : "text-app-muted hover:text-app-primary"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Toggle
            checked={form.isPinned}
            onChange={(v) => set("isPinned", v)}
            icon={Pin}
            title="Pin to top"
            description="Keeps it above newer notices on every dashboard."
          />
          <Toggle
            checked={form.requiresAck}
            onChange={(v) => set("requiresAck", v)}
            icon={ShieldCheck}
            title="Require acknowledgement"
            description="Everyone must confirm they've read it; you can track who has."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <span className={label}>Goes live</span>
            {isEdit && wasLive ? (
              <p className="rounded-lg border border-app-primary px-3 py-2 text-sm text-app-muted">Already published</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-1 rounded-lg bg-app-hover p-1">
                  {[
                    [false, "Now"],
                    [true, "Schedule"],
                  ].map(([value, text]) => (
                    <button
                      key={text}
                      type="button"
                      aria-pressed={form.schedule === value}
                      onClick={() => set("schedule", value)}
                      className={`rounded-md px-2 py-1.5 text-sm font-medium transition-colors ${
                        form.schedule === value ? "bg-app-card text-app-heading shadow-sm" : "text-app-muted hover:text-app-primary"
                      }`}
                    >
                      {text}
                    </button>
                  ))}
                </div>
                {form.schedule && (
                  <input
                    type="datetime-local"
                    aria-label="Publish date and time"
                    value={form.publishAt}
                    min={toLocalInputValue(new Date())}
                    onChange={(e) => set("publishAt", e.target.value)}
                    className={noticeInput}
                  />
                )}
              </div>
            )}
          </div>
          <div>
            <label className="mb-1 flex items-center gap-2 text-sm font-medium text-app-secondary">
              <input
                type="checkbox"
                checked={form.hasExpiry}
                onChange={(e) => set("hasExpiry", e.target.checked)}
                className="h-4 w-4 rounded border-app-primary bg-app-input text-blue-600"
              />
              Take down automatically
            </label>
            {form.hasExpiry ? (
              <input
                type="datetime-local"
                aria-label="Expiry date and time"
                value={form.expiresAt}
                min={form.schedule && form.publishAt ? form.publishAt : toLocalInputValue(new Date())}
                onChange={(e) => set("expiresAt", e.target.value)}
                className={noticeInput}
              />
            ) : (
              <p className="rounded-lg border border-app-primary px-3 py-2 text-sm text-app-muted">Stays up until you delete it</p>
            )}
          </div>
        </div>

        <div>
          <span className={label}>
            Attachments <span className="font-normal text-app-muted">(up to {MAX_FILES}, 25 MB each)</span>
          </span>
          {(notice?.attachments?.length > 0 || files.length > 0) && (
            <ul className="mb-2 space-y-1.5">
              {(notice?.attachments || []).map((a) => {
                const removed = removeIds.includes(a.id);
                return (
                  <li
                    key={`a${a.id}`}
                    className={`flex items-center gap-3 rounded-lg border border-app-primary px-3 py-2 text-sm ${removed ? "opacity-50" : ""}`}
                  >
                    <FileText className="h-4 w-4 shrink-0 text-app-muted" aria-hidden="true" />
                    <span className={`min-w-0 flex-1 truncate ${removed ? "line-through" : ""}`}>{a.fileName}</span>
                    <span className="text-xs text-app-muted">{formatFileSize(a.size)}</span>
                    <button
                      type="button"
                      className="rounded p-1 text-app-muted hover:bg-app-hover hover:text-app-primary"
                      onClick={() =>
                        setRemoveIds((ids) => (removed ? ids.filter((id) => id !== a.id) : [...ids, a.id]))
                      }
                      aria-label={removed ? `Keep ${a.fileName}` : `Remove ${a.fileName}`}
                      title={removed ? "Keep" : "Remove"}
                    >
                      {removed ? <RotateCcw className="h-4 w-4" /> : <X className="h-4 w-4" />}
                    </button>
                  </li>
                );
              })}
              {files.map((file, i) => (
                <li key={`f${i}`} className="flex items-center gap-3 rounded-lg border border-dashed border-blue-400 px-3 py-2 text-sm">
                  <Paperclip className="h-4 w-4 shrink-0 text-blue-600 dark:text-sky-400" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">{file.name}</span>
                  <span className="text-xs text-app-muted">{formatFileSize(file.size)}</span>
                  <button
                    type="button"
                    className="rounded p-1 text-app-muted hover:bg-app-hover hover:text-app-primary"
                    onClick={() => setFiles((list) => list.filter((_, index) => index !== i))}
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {slotsLeft > 0 && (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                addFiles(e.dataTransfer.files);
              }}
              className={`flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed px-4 py-5 text-sm transition-colors ${
                dragging ? "border-blue-500 bg-blue-50 dark:bg-blue-500/10" : "border-app-primary hover:bg-app-hover"
              }`}
            >
              <Upload className="h-5 w-5 text-app-muted" aria-hidden="true" />
              <span className="font-medium text-app-secondary">Drop files here or click to browse</span>
              <span className="text-xs text-app-muted">PDF, Word, Excel, PowerPoint, images, text</span>
            </button>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      </form>
    </NoticeModal>
  );
}

export default NoticeFormModal;
